import { 
  TimelineInitService, 
  TimelineInitData,
  TimelineInitDataProvider,
  Category,
  AssetInstance,
  ClipInstance,
  SupabaseAssetInstance,
  SupabaseClipInstance
} from './types'
import { ASSET_CATEGORIES, ENABLE_CAMERA } from '@/types/assetTypes';

// Initial endTime for background assets at scene-init time. The actual rendered
// width is resolved from the live timelineLength at render time via
// resolveAssetEndTime, so this stored value is cosmetic.
const BACKGROUND_ASSET_INIT_END_TIME = 60;

export class TimelineInit implements TimelineInitService {
  constructor(private readonly dataProvider: TimelineInitDataProvider) {}

  async initializeTimeline(
    sceneId: string, 
    convertToAssetInstance: (asset: SupabaseAssetInstance) => AssetInstance, 
    convertToClipInstance: (clip: SupabaseClipInstance) => ClipInstance
  ): Promise<TimelineInitData> {
    const { assets, clips, scenePayload } = await this.dataProvider.fetchSceneAssetsAndClips(sceneId);

    // Gates (asset instances of category 'trigger') render in the triggers row, not
    // as normal asset rows; they are sourced into the triggers slice separately.
    const sceneAssetsArray = assets.map(convertToAssetInstance).filter((a) => a.instanceCategory !== 'trigger');
    const clipInstances = clips.map(convertToClipInstance);

    return this.buildCategories(sceneAssetsArray, clipInstances, scenePayload);
  }

  async initializeCompleteTimeline(
    sceneId: string,
    convertToAssetInstance: (asset: SupabaseAssetInstance) => AssetInstance, 
    convertToClipInstance: (clip: SupabaseClipInstance) => ClipInstance,
    dispatch: (action: any) => void,
    setSceneAssetsArrayAction: any,
    setClips: (clips: ClipInstance[]) => void
  ): Promise<TimelineInitData> {
    // Fetch data and update state - consolidating the fetching logic
    const { assets, clips, scenePayload } = await this.dataProvider.fetchSceneAssetsAndClips(sceneId);
    
    const convertedAssets = assets.map(convertToAssetInstance).filter((a) => a.instanceCategory !== 'trigger');
    const convertedClips = clips.map(convertToClipInstance);

    // Update Redux state
    dispatch(setSceneAssetsArrayAction(convertedAssets));
    setClips(convertedClips);

    return this.buildCategories(convertedAssets, convertedClips, scenePayload);
  }

  // Public method to rebuild categories from existing data (no fetch)
  buildCategoriesFromData(sceneAssetsArray: AssetInstance[], clips: ClipInstance[], scenePayload?: Record<string, unknown> | null): TimelineInitData {
    return this.buildCategories(sceneAssetsArray, clips, scenePayload);
  }

  private buildCategories(sceneAssetsArray: AssetInstance[], clips: ClipInstance[], scenePayload?: Record<string, unknown> | null): TimelineInitData {
    if (!sceneAssetsArray?.length) {
      return { 
        categories: [], 
        onlyBackgroundAssets: true,
        categoryStates: {
          lockStates: {},
          visibilityStates: {},
          muteStates: {}
        }
      };
    }

    // Helper function to sort assets by display_order within a category
    const sortByDisplayOrder = <T extends { assetInstance: AssetInstance }>(assets: T[]): T[] => {
      return [...assets].sort((a, b) => {
        const orderA = a.assetInstance.instanceDisplayOrder ?? null;
        const orderB = b.assetInstance.instanceDisplayOrder ?? null;
        
        // If both have display_order, sort by it
        if (orderA !== null && orderB !== null) {
          return orderA - orderB;
        }
        // If only A has display_order, A comes first
        if (orderA !== null && orderB === null) {
          return -1;
        }
        // If only B has display_order, B comes first
        if (orderA === null && orderB !== null) {
          return 1;
        }
        // If neither has display_order, maintain original order (already sorted by DB query)
        return 0;
      });
    };

    const newCategories: Category[] = [];

    // Characters Category
    const characterAssets = sortByDisplayOrder(
      sceneAssetsArray
        .filter((asset: AssetInstance) => asset.instanceCategory === 'character')
        .map((asset: AssetInstance) => {
          return {
            duration: asset.instanceEndTime - asset.instanceBeginTime,
            startTime: asset.instanceBeginTime,
            endTime: asset.instanceEndTime,
            imageSrc: asset.instanceImgUrl,
            assetInstance: asset
          };
        })
    );

    // Combine props and images into a single category
    const propsAndImagesAssets = sortByDisplayOrder(
      sceneAssetsArray
        .filter((asset: AssetInstance) => asset.instanceCategory === 'prop' || asset.instanceCategory === 'image' || asset.instanceCategory === 'mask')
        .map((asset) => {
          return {
            duration: asset.instanceEndTime - asset.instanceBeginTime,
            startTime: asset.instanceBeginTime,
            endTime: asset.instanceEndTime,
            imageSrc: asset.instanceCategory === 'image' ? asset.instanceSrcUrl : asset.instanceImgUrl,
            assetInstance: asset
          };
        })
    );

    // Open Brush Category
    const openBrushAssets = sortByDisplayOrder(
      sceneAssetsArray
        .filter((asset: AssetInstance) => asset.instanceCategory === ASSET_CATEGORIES.OPEN_BRUSH)
        .map((asset) => {
          return {
            duration: asset.instanceEndTime - asset.instanceBeginTime,
            startTime: asset.instanceBeginTime,
            endTime: asset.instanceEndTime,
            imageSrc: asset.instanceImgUrl,
            assetInstance: asset
          };
        })
    );

    // Screen UI Category
    const screenUIAssets = sortByDisplayOrder(
      sceneAssetsArray
        .filter((asset: AssetInstance) => asset.instanceCategory === ASSET_CATEGORIES.SCREEN_UI)
        .map((asset: AssetInstance) => {
          return {
            duration: asset.instanceEndTime - asset.instanceBeginTime,
            startTime: asset.instanceBeginTime,
            endTime: asset.instanceEndTime,
            imageSrc: undefined,
            assetInstance: asset
          };
        })
    );

    // Audio Category
    const audioAssets = sortByDisplayOrder(
      sceneAssetsArray
        .filter((asset: AssetInstance) => asset.instanceCategory === ASSET_CATEGORIES.AUDIO)
        .map((asset) => {
          return {
            duration: asset.instanceEndTime - asset.instanceBeginTime,
            startTime: asset.instanceBeginTime,
            endTime: asset.instanceEndTime,
            audioUrl: asset.instanceSrcUrl,
            assetInstance: asset
          };
        })
    );

    // Video Category
    const videoAssets = sortByDisplayOrder(
      sceneAssetsArray
        .filter((asset: AssetInstance) => asset.instanceCategory === ASSET_CATEGORIES.VIDEO)
        .map((asset) => {
          return {
            duration: asset.instanceEndTime - asset.instanceBeginTime,
            startTime: asset.instanceBeginTime,
            endTime: asset.instanceEndTime,
            imageSrc: asset.instanceImgUrl,
            assetInstance: asset
          };
        })
    );

    // Background assets
    const backgroundAssets = sortByDisplayOrder(
      sceneAssetsArray
        // CAMERA_FEATURE: camera gated by ENABLE_CAMERA flag
        .filter((asset: AssetInstance) => asset.instanceCategory === ASSET_CATEGORIES.IMMERSAL || asset.instanceCategory === ASSET_CATEGORIES.GEOSPATIAL || (ENABLE_CAMERA && asset.instanceCategory === ASSET_CATEGORIES.CAMERA) || asset.instanceCategory === ASSET_CATEGORIES.TAP_TO_PLACE || asset.instanceCategory === ASSET_CATEGORIES.IMAGE_ANCHOR)
        .map((asset) => {
          return {
            startTime: 0,
            endTime: BACKGROUND_ASSET_INIT_END_TIME,
            imageSrc: undefined,
            assetInstance: asset
          };
        })
    );

    // Actions Category - Only include clips NOT associated with any asset
    const standaloneClips = clips.filter(
      (clip: ClipInstance) => !clip.assetId // Only include clips that aren't associated with any asset
    );

    const actionsAssets = standaloneClips.map((clip: ClipInstance) => ({
      startTime: clip.instanceStartTime,
      endTime: clip.instanceStartTime + 1, // Default 1 second duration for actions
      action: clip.instanceDisplayName
    }));

    // CAMERA_FEATURE: camera-only scene detection gated by ENABLE_CAMERA flag
    // Check if we only have camera assets (no other meaningful content)
    const onlyCameraAssets = ENABLE_CAMERA && sceneAssetsArray.every(asset => asset.instanceCategory === ASSET_CATEGORIES.CAMERA) &&
                             characterAssets.length === 0 &&
                             propsAndImagesAssets.length === 0 &&
                             openBrushAssets.length === 0 &&
                             screenUIAssets.length === 0 &&
                             audioAssets.length === 0 &&
                             videoAssets.length === 0 &&
                             actionsAssets.length === 0;

    // Check if we only have camera assets (no other background assets or interactive content)
    const onlyBackgroundAssets = ENABLE_CAMERA && backgroundAssets.every(asset => asset.assetInstance.instanceCategory === ASSET_CATEGORIES.CAMERA) &&
                                 characterAssets.length === 0 &&
                                 propsAndImagesAssets.length === 0 &&
                                 openBrushAssets.length === 0 &&
                                 screenUIAssets.length === 0 &&
                                 audioAssets.length === 0 &&
                                 videoAssets.length === 0 &&
                                 actionsAssets.length === 0;

    // If only camera assets exist, show them but indicate they should start collapsed
    if (onlyCameraAssets && backgroundAssets.length > 0) {
      newCategories.push({
        label: 'Anchor',
        type: 'background', 
        items: backgroundAssets
      });
      
      return { 
        categories: newCategories, 
        onlyBackgroundAssets: true, // Timeline should start collapsed for camera-only scenes
        categoryStates: {
          lockStates: {},
          visibilityStates: {},
          muteStates: {}
        }
      };
    }

    // Build all categories (unsorted)
    const allCategories: Category[] = [];
    
    if (characterAssets.length) {
      allCategories.push({
        label: 'Characters',
        type: 'characters',
        items: characterAssets
      });
    }

    if (propsAndImagesAssets.length) {
      allCategories.push({
        label: 'Props',
        type: 'props',
        items: propsAndImagesAssets
      });
    }

    if (openBrushAssets.length) {
      allCategories.push({
        label: 'Open Brush',
        type: 'open_brush',
        items: openBrushAssets
      });
    }

    if (screenUIAssets.length) {
      allCategories.push({
        label: 'Screen Messages',
        type: 'screen_ui',
        items: screenUIAssets
      });
    }

    if (audioAssets.length) {
      allCategories.push({
        label: 'Audio',
        type: 'audio',
        items: audioAssets
      });
    }

    if (videoAssets.length) {
      allCategories.push({
        label: 'Video',
        type: 'video',
        items: videoAssets
      });
    }

    if (backgroundAssets.length) {
      allCategories.push({
        label: 'Anchor',
        type: 'background',
        items: backgroundAssets
      });
    }

    if (actionsAssets.length) {
      allCategories.push({
        label: 'Actions',
        type: 'actions',
        items: actionsAssets
      });
    }

    // Sort categories by order from scene payload
    const categoryOrder = (scenePayload?.categoryOrder as Record<string, number>) || null;
    const defaultOrder: Record<string, number> = {
      'characters': 0,
      'props': 1,
      'open_brush': 2,
      'screen_ui': 3,
      'audio': 4,
      'video': 5,
      'background': 6,
      'actions': 7
    };
    
    const orderMap = categoryOrder || defaultOrder;

    // A scene saved before a category existed has no entry for it, so fall through to the
    // declared default rather than dropping it at the end. Ties (a persisted slot colliding
    // with a newer category's default) resolve by push order, which is the default order.
    const orderOf = (type: string) => orderMap[type] ?? defaultOrder[type] ?? 999;

    const sortedCategories = allCategories.sort((a, b) => orderOf(a.type) - orderOf(b.type));

    return { 
      categories: sortedCategories, 
      onlyBackgroundAssets,
      categoryStates: {
        lockStates: {},
        visibilityStates: {},
        muteStates: {}
      }
    };
  }
}
